import React from 'react';
import { Layout, Button, Divider } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import { getEntityById, nullSafeGet } from '../../utils/DataAccessUtils';
import { proposalsRestCrudThunksForBuyer } from '../../thunks/proposals_thunks';
import {
	appendWorkOrderNotesForBuyer,
	workOrdersRestCrudThunksForBuyer,
} from '../../thunks/work_orders_thunks';
import { disputeQuoteForBuyer, quotesRestCrudThunksForBuyer } from '../../thunks/quotes_thunks';
import { EmptyState } from '../empty_state/EmptyState';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import QuotePageDisplay from '../quote_page_display/QuotePageDisplay';
import OWLink from '../OWLink';
import ProposalAIForm from '../proposal_ai_form/ProposalAIForm';

const { Content } = Layout;

require('./WorkOrdersDetailQuotesPage.less');

class WorkOrderQuotesDetailPage extends React.Component<any, any> {
	state = {
		noSupplier: false,
		quoteId: null,
		quoteExists: false,
	};
	refreshQuote = () => {
		const { getQuote, getWorkOrder, getCount, workOrder, match } = this.props;
		const workOrderId = workOrder.id || nullSafeGet('params.id', match);
		const quote = this.getQuote();
		getQuote(quote.id);
		getCount(workOrderId, 'draft').then((d) => this.setState({ quoteExists: d > 0 }));
		getWorkOrder(quote.workOrder.id);
	};

	getQuote = () => {
		const { quotes } = this.props;
		return getEntityById(this.state.quoteId, quotes);
	};

	// After a quote is created via the embedded upload flow, re-fetch so the newly
	// created quote renders (which unmounts the uploader + drawer).
	refreshQuotesAfterCreate = () => {
		const { getQuotes, getCount, workOrder, match } = this.props;
		const workOrderId = match.params.id;
		getCount(workOrderId, 'draft').then((d) => this.setState({ quoteExists: d > 0 }));
		if (nullSafeGet('supplierFacilityId', workOrder)) {
			getQuotes(
				{ workOrderId, supplierFacilityId: workOrder.supplierFacilityId },
				'workOrderAssociatedQuotes'
			).then((quotes) => this.setState({ quoteId: nullSafeGet('0.id', quotes) }));
		}
	};

	componentDidMount() {
		const { getQuotes, getQuote, getWorkOrder, match, workOrder, getCount } = this.props;
		const workOrderId = match.params.id;
		getCount(workOrderId, 'draft').then((d) => this.setState({ quoteExists: d > 0 }));
		const getQuoteDetails = (workOrder) => {
			if (nullSafeGet('supplierFacilityId', workOrder)) {
				getQuotes(
					{
						workOrderId,
						supplierFacilityId: workOrder.supplierFacilityId,
					},
					'workOrderAssociatedQuotes'
				).then((quotes) => {
					const quoteId = nullSafeGet('0.id', quotes);
					this.setState({ quoteId });
					// The list (retrieveAll) response omits budgets — they are hydrated only on the
					// single-proposal GET, so re-read this quote to get them into the store.
					if (quoteId) {
						getQuote(quoteId);
					}
				});
			} else {
				this.setState({ noSupplier: true });
			}
		};
		if (workOrder && workOrder.id) {
			getQuoteDetails(workOrder);
		} else {
			getWorkOrder(workOrderId).then((data) => {
				getQuoteDetails(data);
			});
		}
	}

	render() {
		const { quoteFetching, quotesUpdating, workOrder, workOrderFetching } = this.props;
		const { noSupplier } = this.state;
		const quote = this.getQuote();
		const emptyGraphic = (
			<img
				style={{ marginBottom: 8 }}
				src="https://s3.amazonaws.com/mock-data-assets/categories/images/bank terminal.svg"
				alt="It's a little lonely in here."
			/>
		);
		return (
			<AccessPermissionChecker name={PERMISSION_NAMES.CAN_VIEW_QUOTES} isPage>
				{workOrderFetching || (quoteFetching && !noSupplier) ? (
					<PageLoadingPlaceholder />
				) : nullSafeGet('id', quote) && !noSupplier ? (
					<Content className="workOrderQuoteDetailsPage">
						<QuotePageDisplay
							key={quote.id}
							quote={quote}
							refreshQuote={this.refreshQuote}
							showViewQuotePage={true}
							quotesUpdating={quotesUpdating}
							displayComparison={true}
						/>
					</Content>
				) : !noSupplier && !this.state.quoteExists ? (
					<AccessPermissionChecker
						name={PERMISSION_NAMES.CAN_UPLOAD_QUOTES_FOR_SUPPLIER}
						permissionDeniedContent={
							<EmptyState
								graphic={emptyGraphic}
								headline={"You haven't received a quote for this work order yet."}
							/>
						}
					>
						<div className="mx-auto mt-[120px] flex max-w-[50%] flex-col items-center justify-center gap-6">
							<ProposalAIForm onCreated={this.refreshQuotesAfterCreate} />
							<Divider style={{ margin: 0 }}>
								<span className="text-character-secondary">OR</span>
							</Divider>
							<OWLink
								sameParent
								to={`/buyer/workOrders/detail/${nullSafeGet('id', workOrder)}/newProposal`}
							>
								<Button>Create from scratch</Button>
							</OWLink>
						</div>
					</AccessPermissionChecker>
				) : (
					<EmptyState
						graphic={emptyGraphic}
						headline={"You haven't received a quote for this work order yet."}
						body={
							!noSupplier && this.state.quoteExists ? (
								<span>
									Your service provider has drafted a quote, it just hasn't been published yet.
								</span>
							) : null
						}
					/>
				)}
			</AccessPermissionChecker>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	quoteFetching: state.proposals.fetching,
	quotesUpdating: state.proposals.updating,
	quote: state.proposals.detail,
	quotes: state.proposals,
	currentUser: state.session.currentUser,
	match: ownProps.match,
	history: ownProps.history,
	workOrder: getEntityById(ownProps.match.params.id, state.work_orders),
	workOrderFetching: state.work_orders.fetching,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	appendWorkOrderNotes: (entity) => dispatch(appendWorkOrderNotesForBuyer(entity)),
	getWorkOrder: (id) => dispatch(workOrdersRestCrudThunksForBuyer.readOne(id)),
	getQuote: (id) => dispatch(quotesRestCrudThunksForBuyer.readOne(id)),
	disputeQuote: (id) => dispatch(disputeQuoteForBuyer({ id })),
	getCount: (woId, status) =>
		dispatch(
			proposalsRestCrudThunksForBuyer.countBy({ workOrderId: woId, status: status }, status)
		),
	getQuotes: (params, targetCollectionName) =>
		dispatch(proposalsRestCrudThunksForBuyer.read(params, targetCollectionName)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(WorkOrderQuotesDetailPage));
